// [S-411 / E-MULTI-TENANT] Active-tenant context.
//
// Split into its own module (no JSX) so BOTH the provider that owns the state
// (ActiveTenantProvider) AND useSession() in AuthProvider can import it without
// a circular dependency. useSession derives the effective per-tenant role from
// the active membership, so every screen that already reads session.role /
// session.studioId reacts to a tenant switch automatically.
import { createContext, useContext } from "react";
import type { AccessTier, Capability, Role } from "@csai/core/browser";
import type { WebSession } from "./types";

export type WebMembership = {
  /** Full tenant id, e.g. `tenant-acme`. */
  tenantId: string;
  /** The caller's role IN THIS tenant (may differ per tenant). */
  role: Role;
  accessTier: AccessTier;
  /**
   * E-RBAC / S-313 — the EFFECTIVE capability set in this tenant (a custom
   * role's grant, or the built-in role's default resolved server-side). Absent
   * on a payload predating the capability field → the UI falls back to the base
   * role's default via effectiveCapabilities().
   */
  capabilities?: readonly Capability[];
};

export type ActiveTenantContextValue = {
  /** All tenants the signed-in user belongs to (>1 → show the switcher). */
  memberships: WebMembership[];
  /** The tenant currently in effect (full id), or null (single-tenant / not yet resolved). */
  activeTenantId: string | null;
  /** Select a different tenant the user is a member of. */
  switchTenant: (tenantId: string) => void;
  /** True while memberships are loading. */
  loading: boolean;
};

export const ActiveTenantContext =
  createContext<ActiveTenantContextValue | null>(null);

/** Read the active-tenant context. Returns null when not inside the provider. */
export function useActiveTenant(): ActiveTenantContextValue | null {
  return useContext(ActiveTenantContext);
}

/**
 * Pure derivation of the EFFECTIVE session for the active tenant. The login
 * token carries the user's claims for ONE tenant; when they belong to several
 * and have switched, override role/studioId/tenantNamespace/accessTier with the
 * active tenant's membership. Kept pure (no React) so it's directly testable and
 * reused by useSession.
 *   - platform_admin (studioId === null) is global → never overridden.
 *   - No context / no active tenant → base session unchanged (back-compat).
 *   - Active tenant not in the membership list → surface the id (so the API
 *     layer sends it; the backend validates membership) but keep base role.
 */
export function deriveEffectiveSession(
  base: WebSession,
  active: ActiveTenantContextValue | null
): WebSession {
  if (base.studioId === null) return base;
  const activeTenantId = active?.activeTenantId ?? null;
  if (active === null || activeTenantId === null) return base;
  const membership = active.memberships.find(
    (m) => m.tenantId === activeTenantId
  );
  if (membership === undefined) return { ...base, activeTenantId };
  const studioId = activeTenantId.replace(/^tenant-/, "");
  return {
    ...base,
    role: membership.role,
    studioId,
    tenantNamespace: `studio-${studioId}`,
    accessTier: membership.accessTier,
    // S-313 — carry the active tenant's effective capabilities so UI gates
    // (sessionCan) reflect a per-tenant custom role, not the login token's set.
    // Undefined here (older payload) → drop the key so effectiveCapabilities()
    // falls back to this membership's built-in role default.
    ...(membership.capabilities !== undefined
      ? { capabilities: membership.capabilities }
      : { capabilities: undefined }),
    activeTenantId,
  };
}
